import parseHTML, { domToReact } from "html-react-parser"
import { cx } from "class-variance-authority"
import get from "lodash/get"
import find from "lodash/find"
import React from "react"
import withSuspense from "../utils/withSuspense"
import {
  columnsTransformer,
  dividerTransformer,
  headingTransformer,
  imageItemTransformer,
  inlineAsideTransformer,
  linkCardTransformer,
  linkCardsTransformer,
  linkDescriptionTransformer,
  testimonialTransformer,
  mediaTileTransformer,
  quickLinkTransformer,
  solutionTransformer,
} from "../utils/transformers"

import { BodyStylesProps } from "./BodyStyles"
import { SpacingProps } from "./Spacing"
import { ColumnsProps } from "./Columns"
import { SectionProps } from "./Section"
import ContentSwitcher from "./ContentSwitcher"
import SolutionTile, {
  SolutionTileProps,
} from "../patterns/SolutionTile/SolutionTile"

const LinkWrapper = withSuspense(React.lazy(() => import("./LinkWrapper")))

const AuthorBox = withSuspense(React.lazy(() => import("./AuthorBox")))
const BlockLinkGroup = withSuspense(
  React.lazy(() => import("./BlockLinkGroup"))
)
const BlockTabs = withSuspense(React.lazy(() => import("./BlockTabs")))
const Callout = withSuspense(React.lazy(() => import("./Callout")))
const CalloutGroup = withSuspense(React.lazy(() => import("./CalloutGroup")))
const Card = withSuspense(React.lazy(() => import("./Card")))
const CallToAction = withSuspense(React.lazy(() => import("./CallToAction")))
const Carousel = withSuspense(React.lazy(() => import("./Carousel")))
const Columns = withSuspense(React.lazy(() => import("./Columns")))
const Divider = withSuspense(React.lazy(() => import("./Divider")))
const Heading = withSuspense(React.lazy(() => import("./Heading")))
const ImageBlock = withSuspense(React.lazy(() => import("./BlockImage")))
const ImageOptim = withSuspense(React.lazy(() => import("./ImageOptim")))
const InfoBox = withSuspense(React.lazy(() => import("./InfoBox")))
const InlineAside = withSuspense(React.lazy(() => import("./InlineAside")))
const LinkCard = withSuspense(React.lazy(() => import("./LinkCard")))
const LinkCards = withSuspense(React.lazy(() => import("./LinkCards")))
const LinkDescription = withSuspense(
  React.lazy(() => import("./LinkDescription"))
)
const LinkElement = withSuspense(React.lazy(() => import("./LinkElement")))
const MediaBox = withSuspense(React.lazy(() => import("./MediaBox")))
const MediaTile = withSuspense(React.lazy(() => import("./MediaTile")))
const Spacing = withSuspense(React.lazy(() => import("./Spacing")))
const Testimonial = withSuspense(React.lazy(() => import("./Testimonial")))
const TextAndAside = withSuspense(React.lazy(() => import("./TextAndAside")))
const Video = withSuspense(React.lazy(() => import("./Video")))
const BlockText = withSuspense(React.lazy(() => import("./BlockText")))
const Links = withSuspense(React.lazy(() => import("./Links")))
const Gallery = withSuspense(React.lazy(() => import("./Gallery")))
const QuickLink = withSuspense(React.lazy(() => import("./QuickLink")))

export interface RichTextProps {
  /**
   * The content to be rendered inside the Rich Text element
   */
  content: any
  /**
   * Content items used to render components in the content
   */
  linkedItems?: any[]
  /**
   * Content items used to render links in the content
   */
  links?: any[]
  /**
   * The background color of the section
   */
  backgroundColor?: SectionProps["backgroundColor"]
  /**
   * If the section should have a contrast theme
   */
  contrast?: boolean
  /**
   * The current page’s colorScheme. Passed to modal component so content
   * matches page.
   */
  colorScheme?: BodyStylesProps["colorScheme"]
  /**
   * Current language of the website
   * Language is now handled by the URL resolver, but this can be used
   * if you need to make layout changes based on the language
   */
  currentLanguage?: "en" | "de" | "es" | "fr" | "it" | "pl" | "pt" | "jp" | "cn"
  /**
   * The maximum width of the section container
   */
  maxContainerWidth?: number
  /**
   * The alignment of the text in the section
   */
  textAlign?: "left" | "center"
  /**
   * The size of the text in the section
   */
  textSize?: "xs" | "sm" | "base" | "xl"
  /**
   * The styling of blocks of text in the section
   * - **article** – transforms `h3` headings to uppercase and adds cyan headings for `h3` headings and gray headings for `h4` headings.
   * - **compact** – reduces heading sizes and space between elements. Typically used in modals or switchers where the base text sizes and spacing doesn’t look right
   * - **landingPage** – bolds items in lists so they are more prominent on the page
   */
  theme?: BodyStylesProps["theme"]
  /**
   * The analytics region of the section
   */
  region?: string
  /**
   * The spacing between elements in the section
   */
  spacing?: SpacingProps["spacing"]
  /**
   * The width of gutters in the section
   */
  gutterWidth?: ColumnsProps["gutterWidth"]
}

const RichText = ({
  content,
  linkedItems,
  links,
  backgroundColor,
  contrast,
  colorScheme,
  currentLanguage,
  maxContainerWidth,
  textAlign,
  textSize,
  theme,
  region,
  spacing,
  gutterWidth,
}: RichTextProps) => {
  if (!content || !content.length) {
    return null
  }

  // Remove any line breaks from HTML.
  const cleaned = content.replace(/(<p><br><\/p>|\n|\r)+/, "")

  // Parse HTML as React components, replacing any content items.
  const children = parseHTML(cleaned, {
    replace: (domNode) =>
      replaceLinkedItems(
        domNode,
        linkedItems,
        links,
        backgroundColor,
        contrast,
        colorScheme,
        currentLanguage,
        maxContainerWidth,
        textAlign,
        textSize,
        theme,
        region,
        spacing,
        gutterWidth
      ),
  })

  // Return all components inside a fragment.
  return <>{children}</>
}

export default RichText

// Get code name for Kentico Cloud inline content item from DOM node.
const getCodeName = (domNode) => {
  return get(domNode, 'attribs["data-codename"]') || null
}

// Get data for Kentico Cloud inline content item.
const getLinkedItem = (codename, linkedItems) => {
  return find(linkedItems, (item) => {
    if (!get(item, "system.codename")) {
      console.log(item.__typename)
    }
    return get(item, "system.codename") === codename
  })
}

// Get href for Kentico Cloud inline link from DOM node
const getLinkHref = (domNode) => {
  return domNode.attribs && domNode.attribs["href"]
}

// Get href for content item from item in links array
const getLinkFromContentItem = (domNode, links, currentLanguage) => {
  const linkId = domNode.attribs && domNode.attribs["data-item-id"]
  const link = links && links.find((link) => link.link_id === linkId)

  if (link) {
    return get(link, "modular_content.resolved_url") || getLinkHref(domNode)
  }

  return getLinkHref(domNode)
}

// Get content item for link from links array
const getContentItemFromDomNode = (domNode, links) => {
  const linkId = domNode.attribs && domNode.attribs["data-item-id"]
  const contentItem = links && links.find((link) => link.link_id === linkId)

  return contentItem
}

// Check if DOM node is a Kentico Cloud inline link.
const isLink = (domNode) => {
  return (
    domNode.name === "a" && domNode.attribs && domNode.attribs["data-item-id"]
  )
}

// Check if DOM node is a table.
const isTable = (domNode) => {
  return domNode.name === "table"
}

const isFile = (domNode) => {
  return (
    domNode.name === "a" && domNode.attribs && domNode.attribs["data-asset-id"]
  )
}

const isLinkedItem = (domNode) => {
  return (
    domNode.name === "object" &&
    !!domNode.attribs &&
    domNode.attribs.type === "application/kenticocloud"
  )
}

// Replace HTML DOM node with React component.
const replaceLinkedItems = (
  domNode,
  linkedItems,
  links,
  backgroundColor,
  contrast,
  colorScheme,
  currentLanguage,
  maxContainerWidth,
  textAlign,
  textSize,
  theme,
  region,
  spacing,
  gutterWidth
) => {
  /**
   * Replaces inline links to content items and assets with a link component or a video component if the link is to a video item.
   * @param domNode - The DOM node to replace
   */
  const replaceLinks = (domNode) => {
    let linkTo = isLink
      ? getLinkFromContentItem(domNode, links, currentLanguage)
      : getLinkHref(domNode)
    // Converts the children of the link component to React (this might be text or other tags (like `strong`, `em`) and their children)
    const children = domToReact(domNode.children)
    // The data for the corresponding content item
    const contentItem = getContentItemFromDomNode(domNode, links)

    // If the link is to a video item, render the video as a link that opens the video in a modal
    if (contentItem?.modular_content?.system?.type === "video") {
      return (
        <Video
          coverImageCropWidth={maxContainerWidth}
          format="link"
          video={contentItem.modular_content}
          linkLabel={children}
          playInModal={true}
        />
      )
    }

    // Otherwise, render the link using our link component
    return (
      <LinkWrapper
        to={linkTo}
        data-analytics-title={get(contentItem, "modular_content.system.name")}
      >
        {children}
      </LinkWrapper>
    )
  }

  // Replace inline links to content items and assets
  if (isLink(domNode) || isFile(domNode)) {
    return replaceLinks(domNode)
  }

  // Wrap table in wrapper so it scrolls on smaller screens
  // to prevent unwanted x-axis overflow.
  if (isTable(domNode)) {
    return (
      <div className="responsive-table">
        {/* Parses the content in the table, replacing links to content items and assets the same way links are handled in other rich text */}
        {domToReact([domNode], {
          replace: (domNode) => {
            if (isLink(domNode) || isFile(domNode)) {
              return replaceLinks(domNode)
            }
          },
        })}
      </div>
    )
  }

  // Replace modular content with React components
  if (isLinkedItem(domNode)) {
    const codename = getCodeName(domNode)

    if (!codename) {
      return false
    }

    const linkedItem = getLinkedItem(codename, linkedItems)

    if (!linkedItem) {
      return false
    }

    const linkedItemType = get(linkedItem, "system.type")

    if (linkedItemType === "divider") {
      return <Divider {...dividerTransformer(linkedItem)} />
    }

    if (linkedItemType === "aside") {
      return (
        <InlineAside
          {...inlineAsideTransformer(linkedItem)}
          colorScheme={colorScheme}
          currentLanguage={currentLanguage}
          theme={theme}
        />
      )
    }

    if (linkedItemType === "heading") {
      return (
        <Heading
          {...headingTransformer(linkedItem)}
          colorScheme={colorScheme}
          textAlign={textAlign}
        />
      )
    }

    if (linkedItemType === "link_item") {
      // The #main-aside is used as an achor link when there is important content in the sidebar
      // that gets pushed to the bottom of the page at small views. You can find an example of
      // this with the lead gen form on the EV landing page /industries/automotive/ev-battery-conveyors
      let linkHref = get(linkedItem, "elements.link_to.value[0].resolved_url")

      return (
        <p
          className={`text-base ${
            linkHref === "#main-aside" ? "md:hidden" : null
          }`}
        >
          <LinkElement
            colorScheme={colorScheme}
            contentItem={linkedItem}
            contrast={contrast}
            theme={theme}
          />
        </p>
      )
    }

    // override spacing for text block elements
    spacing = linkedItemType === "text_block" ? "sm" : spacing

    return (
      <Spacing
        spacing={spacing}
        className={cx({
          "c-spacer-lg": linkedItemType === "text_and_aside",
        })}
      >
        <LinkedItem
          linkedItem={linkedItem}
          backgroundColor={backgroundColor}
          colorScheme={colorScheme}
          contrast={contrast}
          currentLanguage={currentLanguage}
          maxContainerWidth={maxContainerWidth}
          textAlign={textAlign}
          textSize={textSize}
          theme={theme}
          region={region}
          gutterWidth={gutterWidth}
        />
      </Spacing>
    )
  }
}

export interface LinkedItemProps {
  linkedItem: any
  backgroundColor: RichTextProps["backgroundColor"]
  colorScheme: RichTextProps["colorScheme"]
  contrast: RichTextProps["contrast"]
  currentLanguage: RichTextProps["currentLanguage"]
  gutterWidth: RichTextProps["gutterWidth"]
  maxContainerWidth: RichTextProps["maxContainerWidth"]
  textAlign: RichTextProps["textAlign"]
  textSize: RichTextProps["textSize"]
  theme: RichTextProps["theme"]
  region: RichTextProps["region"]
}

const LinkedItem = ({
  linkedItem,
  backgroundColor,
  colorScheme,
  contrast,
  currentLanguage,
  gutterWidth,
  maxContainerWidth,
  textAlign,
  textSize,
  theme,
  region,
}: LinkedItemProps) => {
  switch (linkedItem.system.type) {
    case "author":
      return <AuthorBox contentItem={linkedItem} />
    case "callout":
      return (
        <Callout
          contentItem={linkedItem}
          currentLanguage={currentLanguage}
          textAlign={textAlign}
        />
      )
    case "callout_group":
      return (
        <CalloutGroup
          colorScheme={colorScheme}
          contentItem={linkedItem}
          currentLanguage={currentLanguage}
          textAlign={textAlign}
          // Overrides text size in instances (usually text & aside) where the default text size looks cramped
          // TODO: Evaluate whether callout items in asides in articles should use smaller text size in all cases
          textSize={["modal", "aside"].indexOf(region) !== -1 ? `xs` : textSize}
        />
      )
    case "call_to_action":
      return (
        <CallToAction
          contentItem={linkedItem}
          currentLanguage={currentLanguage}
          textSize={textSize}
        />
      )
    case "card":
      return (
        <Card>
          <RichText
            region={region}
            content={get(linkedItem, "elements.content.value")}
            linkedItems={get(linkedItem, "elements.content.modular_content")}
            links={get(linkedItem, "elements.content.links")}
          />
        </Card>
      )
    case "carousel":
      const items = get(linkedItem, "elements.items.value") || []
      const hasTestimonial = items.some(
        (item) => item?.system?.type === "testimonial"
      )

      return (
        <Carousel
          backgroundColor={backgroundColor}
          block={linkedItem}
          contentType={hasTestimonial ? "testimonial" : "media"}
          language={currentLanguage}
        />
      )
    case "columns":
      // Pick the columns prop from the transformer and pass the rest of the props to the Columns component
      const { columns, ...columnsProps } = columnsTransformer(linkedItem)
      // Get the type of each column and render the appropriate component
      const columnComponents = columns.map((columnItem) => {
        const columnItemType = get(columnItem, "system.type")

        switch (columnItemType) {
          case "callout":
            return (
              <Callout
                contentItem={columnItem}
                language={currentLanguage}
                textAlign={textAlign}
                style="text"
                direction="row"
              />
            )
          case "callout_group":
            return (
              <CalloutGroup
                colorScheme={colorScheme}
                contentItem={columnItem}
                currentLanguage={currentLanguage}
                textAlign={textAlign}
                textSize={`xs`}
              />
            )
          case "card":
            return (
              <Card>
                <RichText
                  region={region}
                  content={get(columnItem, "elements.content.value")}
                  linkedItems={columnItem}
                  links={get(columnItem, "elements.content.links")}
                />
              </Card>
            )
          case "call_to_action":
            return (
              <CallToAction
                contentItem={columnItem}
                currentLanguage={currentLanguage}
                textSize={textSize}
              />
            )
          case "image":
            return (
              <ImageOptim
                {...imageItemTransformer(columnItem)}
                cropWidth={maxContainerWidth}
                region={region}
                theme={theme}
              />
            )
          case "testimonial":
            return (
              <Testimonial
                {...testimonialTransformer(columnItem)}
                position={textAlign}
              />
            )
          case "text_block":
            return (
              <BlockText
                block={columnItem}
                colorScheme={colorScheme}
                currentLanguage={currentLanguage}
                textAlign={textAlign}
                theme={theme}
              />
            )
          case "quick_link_v2":
            return (
              <QuickLink
                {...quickLinkTransformer(columnItem)}
                iconPosition="top"
              />
            )
          case "video":
            return (
              <Video
                coverImageCropWidth={maxContainerWidth}
                video={columnItem}
                currentLanguage={currentLanguage}
                playInModal={true}
              />
            )
          default:
            return <p style={{ background: `red` }}>{columnItemType}</p>
        }
      })

      return (
        <Columns
          columns={columnComponents}
          {...columnsProps}
          gutterWidth={gutterWidth}
        />
      )
    case "divider":
      return <Divider {...dividerTransformer(linkedItem)} />
    case "gallery":
      return (
        <Gallery
          itemsPerRow={get(
            linkedItem,
            "elements.items_per_row.value[0].codename"
          )}
          mediaItems={get(linkedItem, "elements.media.value")}
          maxContainerWidth={maxContainerWidth}
        />
      )
    case "info_box":
      return (
        <InfoBox contentItem={linkedItem} currentLanguage={currentLanguage} />
      )
    case "image_block":
      return (
        <ImageBlock block={linkedItem} maxContainerWidth={maxContainerWidth} />
      )
    case "image_element":
      return (
        <ImageOptim
          {...imageItemTransformer(linkedItem)}
          cropWidth={maxContainerWidth}
          region={region}
          theme={theme}
        />
      )
    case "link_description":
      return (
        <LinkDescription
          {...linkDescriptionTransformer(linkedItem)}
          colorScheme={colorScheme}
          currentLanguage={currentLanguage}
          border={["modal", "aside"].indexOf(region) !== -1 ? true : false}
          textSize={
            ["modal", "aside"].indexOf(region) !== -1 ? `sm` : undefined
          }
          theme={theme}
        />
      )
    case "link_card":
      return (
        <LinkCard
          {...linkCardTransformer(linkedItem)}
          size="standalone"
          colorScheme={colorScheme}
        />
      )
    case "link_cards":
      return (
        <LinkCards
          linkCards={linkCardsTransformer(linkedItem)}
          columns={"5"}
          size="compact"
        />
      )
    case "link_group":
      return (
        <BlockLinkGroup
          block={linkedItem}
          colorScheme={colorScheme}
          currentLanguage={currentLanguage}
          region={region}
        />
      )
    case "links":
      const links = get(linkedItem, "elements.links.value")
      const alignment = get(linkedItem, "elements.alignment.value[0].codename")
      return (
        <Links
          links={links}
          colorScheme={colorScheme}
          contrast={contrast}
          theme={theme}
          alignment={alignment}
        />
      )
    case "media_box":
      return (
        <MediaBox contentItem={linkedItem} currentLanguage={currentLanguage} />
      )
    case "media_tile":
      return (
        <MediaTile
          colorScheme={colorScheme}
          {...mediaTileTransformer(linkedItem)}
          currentLanguage={currentLanguage}
          maxContainerWidth={maxContainerWidth}
          theme={theme}
        />
      )
    case "tabs":
      return <BlockTabs block={linkedItem} currentLanguage={currentLanguage} />
    case "section":
      // TODO: Consider moving to a component
      const sectionBackgroundColor =
        get(linkedItem, "elements.background_color.value[0].codename") ||
        backgroundColor
      const sectionTextAlign =
        get(linkedItem, "elements.text_align.value[0].codename") || textAlign

      const sectionBackgroundColorClassName = {
        gray_100: "bg-gray-100",
        white: "bg-white",
      }[sectionBackgroundColor]

      const sectionTextAlignClassName = {
        center: "text-center",
      }[sectionTextAlign]

      return (
        <section
          className={`rounded-lg px-12 py-12 ${
            sectionBackgroundColorClassName
              ? sectionBackgroundColorClassName
              : ``
          } ${sectionTextAlignClassName ? sectionTextAlignClassName : ``} `}
        >
          <RichText
            content={get(linkedItem, "elements.content.value")}
            backgroundColor={sectionBackgroundColor}
            linkedItems={get(linkedItem, "elements.content.modular_content")}
            links={get(linkedItem, "elements.content.links")}
            textAlign={sectionTextAlign}
          />
        </section>
      )
    case "testimonial":
      return (
        <Testimonial
          {...testimonialTransformer(linkedItem)}
          position={textAlign}
        />
      )
    case "text_block":
      return (
        <BlockText
          block={linkedItem}
          colorScheme={colorScheme}
          currentLanguage={currentLanguage}
          textAlign={textAlign}
        />
      )
    case "text_and_aside":
      return (
        <TextAndAside
          colorScheme={colorScheme}
          contentItem={linkedItem}
          currentLanguage={currentLanguage}
          maxContainerWidth={maxContainerWidth}
          textSize={textSize}
          theme={theme}
        />
      )
    case "video":
      return (
        <Video
          coverImageCropWidth={maxContainerWidth}
          video={linkedItem}
          currentLanguage={currentLanguage}
          playInModal={["aside"].indexOf(region) !== -1}
        />
      )
    case "content_switcher":
      const solutions = linkedItem.elements.switcher_item.value.map((value) =>
        solutionTransformer(value)
      )
      return (
        <div className="text-gray-900 text-base">
          <ContentSwitcher
            param="solution"
            contentItems={solutions.map((item: SolutionTileProps) => ({
              slug: item.urlSlug,
              buttonText: item.eyebrow,
              buttonProps: {
                "data-analytics-title": item.analyticsTitle,
              },
              children: <SolutionTile {...item} />,
            }))}
          />
        </div>
      )

    default:
      return <p style={{ background: `red` }}>{linkedItem.system.type}</p>
  }
}
